/**
 * DETAIL TABS — striscia di tab della colonna centrale delle schede di dettaglio,
 * con le tab che i plugin contribuiscono nello slot `detail-center:tab` (TS-107).
 *
 * ## Perché un componente e non tre blocchi
 *
 * Person, Business e Quotation avevano la stessa striscia scritta a mano, e solo
 * il preventivo rendeva anche i contributi: le tre schede erano già divergenti su
 * un comportamento che deve essere identico. Qui vive l'unica implementazione.
 *
 * ## Il core non sa cosa monta
 *
 * Del contributo si conoscono `labelKey` — che traduce il core, perché
 * l'intestazione la rende lui — e un `ComponentType<{ entityId: number }>`. I dati
 * se li procura il plugin: è così che la dipendenza resta a senso unico.
 *
 * ## Stato locale, non URL
 *
 * La tab attiva non finisce nell'URL: il segmento `:section` delle schede
 * significa "filtro della timeline" e deve continuare a significare solo quello.
 * Conseguenza accettata: ricaricando la pagina si torna sulla tab core.
 */

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useSlotContributors } from '../plugin/useSlotContributors';

/** Valore della tab del core. I contributi usano `plugin:<key>`, che non collide. */
const CORE_TAB = 'core';

interface DetailTabsProps {
  /** Entità della scheda ospite: filtra i contributi. Es. 'Person', 'Business', 'Quotation'. */
  entityType: string;
  /** Id passato al componente del plugin. */
  entityId: number;
  /** Etichetta GIÀ TRADOTTA della tab core: il testo è della scheda, non di qui. */
  coreLabel: string;
  /** Contenuto della tab core. */
  children: ReactNode;
}

export function DetailTabs({ entityType, entityId, coreLabel, children }: DetailTabsProps) {
  const { t } = useTranslation();
  const contributors = useSlotContributors(entityType, 'detail-center:tab');
  const [activeTab, setActiveTab] = useState<string>(CORE_TAB);

  // La tab attiva può SPARIRE sotto i piedi: i contributi dipendono dai plugin
  // abilitati, che sono reattivi sulla sessione. Senza questo ripiego MUI resterebbe
  // con un `value` che non corrisponde a nessuna tab — nessuna selezionata e un
  // warning in console.
  const active = contributors.some((c) => `plugin:${c.key}` === activeTab) ? activeTab : CORE_TAB;

  return (
    <>
      {/*
        `--multi` quando c'è più di una tab, e non è cosmetica. Con la sola tab
        core la striscia è resa come TITOLO in posizione assoluta — "Attività" in
        serif 22px sopra lo spazio vuoto a sinistra della riga di ricerca, che le
        riserva 150px di padding. Quel trucco regge finché la tab è una: appena un
        plugin ne aggiunge un'altra la striscia cresce verso destra, esce dai
        150px e finisce SOPRA ricerca ed "espandi tutto", che stanno in flusso.

        Il conteggio lo sa solo questo componente, quindi la classe la mette lui:
        in CSS servirebbe `:has()` per contare i figli, più fragile e molto meno
        leggibile.
      */}
      <Tabs
        value={active}
        onChange={(_, v) => setActiveTab(v as string)}
        className={contributors.length > 0 ? 'detail-tabs--multi' : undefined}
      >
        <Tab value={CORE_TAB} label={coreLabel} className="tab-lowercase" />
        {contributors.map((c) => (
          <Tab
            key={c.key}
            value={`plugin:${c.key}`}
            label={c.labelKey ? t(c.labelKey) : c.key}
            className="tab-lowercase"
          />
        ))}
      </Tabs>

      {active === CORE_TAB && children}

      {contributors
        .filter((c) => active === `plugin:${c.key}`)
        .map((c) => <c.component key={c.key} entityId={entityId} />)}
    </>
  );
}
